<template>
  <div class="avatar-circle" :class="{ faded: variant === 'faded' }" data-test="user-avatar">
    <Tooltip tag="div" :text="tooltipText" style="display: inline-block" placement="bottom">
      <div>
        {{ userInitials }}
      </div>
    </Tooltip>
  </div>
</template>

<script setup lang="ts">
import { Tooltip } from "uiv";
import { computed } from "vue";
import { UserProfileApiModel } from "../../api-models";
import leaderTypeColours from "../../services/utilities/leadertype-colours";

type AvatarVariant = "default" | "faded";

type Props = {
  user: UserProfileApiModel;
  size?: string;
  variant?: AvatarVariant;
  tooltipText?: string;
};

const props = withDefaults(defineProps<Props>(), {
  size: "40px",
  variant: "default",
  tooltipText: "",
});

const userInitials = computed(() =>
  `${props.user.firstName[0]}${props.user.lastName[0]}`.toUpperCase(),
);

const tooltipText = computed(() => {
  if (props.tooltipText !== "") return props.tooltipText;
  return `${props.user.firstName} ${props.user.lastName}`;
});

const linearGradient = computed((): string => {
  if (!props.user.bestFitLeaderTypes || props.user.bestFitLeaderTypes.length < 2) return "#404040";

  const primaryColour = leaderTypeColours[props.user.bestFitLeaderTypes[0]];
  const secondaryColour = leaderTypeColours[props.user.bestFitLeaderTypes[1]];
  return `linear-gradient(135deg, ${primaryColour}, ${primaryColour} 50%, ${secondaryColour} 50%)`;
});
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.avatar-circle {
  width: v-bind(size);
  height: v-bind(size);
  border-radius: 100vmax;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  color: white;
  text-transform: uppercase;
  background: v-bind(linearGradient);
  flex-shrink: 0;

  &.faded {
    filter: grayscale(100%) opacity(50%);
  }
}
</style>
